import React, { useState, useEffect } from 'react';
import { apiClient } from '../api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

interface OverviewMetrics {
  documents_today: number;
  revenue_today: number;
  users_active_today: number;
  shops_active_today: number;
  total_registered_shops: number;
  documents_last_hour: number;
  revenue_last_hour: number;
  success_rate: number;
  growth_rate: number;
}

interface GeographicMetric {
  location: string;
  documents: number;
  revenue: number;
  shops: number;
  success_rate: number;
}

interface ShopRanking {
  shop_id: number;
  shop_name: string;
  city: string;
  rank: number;
  documents: number;
  revenue: number;
  customers: number;
  rating: number;
  success_rate: number;
  growth_rate: number;
}

interface RevenueData {
  date: string;
  printing_revenue: number;
  subscription_revenue: number;
  ad_revenue: number;
  total_revenue: number;
}

const AdminAnalytics: React.FC = () => {
  const [overview, setOverview] = useState<OverviewMetrics | null>(null);
  const [shopRankings, setShopRankings] = useState<ShopRanking[]>([]);
  const [revenueData, setRevenueData] = useState<RevenueData[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'shops' | 'revenue' | 'geographic'>('overview');

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);

      // Fetch overview metrics
      const overviewRes = await apiClient.get('/api/v1/admin/analytics/overview');
      setOverview(overviewRes.data);

      // Fetch shop rankings
      const shopsRes = await apiClient.get('/api/v1/admin/analytics/shops/rankings?limit=10');
      setShopRankings(shopsRes.data);

      // Fetch revenue data
      const revenueRes = await apiClient.get('/api/v1/admin/analytics/revenue/overview');
      setRevenueData(revenueRes.data.time_series || []);

      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Analytics Dashboard</h1>
          <p className="text-gray-600 mt-2">Comprehensive business metrics and insights</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {['overview', 'shops', 'revenue', 'geographic'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab as any)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm capitalize
                  ${activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && overview && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <StatCard
                title="Documents Today"
                value={overview.documents_today}
                subtitle={`${overview.documents_last_hour} in last hour`}
                icon="📄"
              />
              <StatCard
                title="Revenue Today"
                value={`₹${overview.revenue_today.toLocaleString()}`}
                subtitle={`₹${overview.revenue_last_hour.toFixed(0)} in last hour`}
                icon="💰"
              />
              <StatCard
                title="Active Users"
                value={overview.users_active_today}
                subtitle="Today"
                icon="👥"
              />
              <StatCard
                title="Active Shops"
                value={overview.shops_active_today}
                subtitle="Today"
                icon="🏪"
              />
            </div>

            {/* Performance Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Success Rate</h3>
                <div className="flex items-center justify-center">
                  <div className="text-5xl font-bold text-green-600">
                    {overview.success_rate.toFixed(1)}%
                  </div>
                </div>
                <p className="text-center text-gray-600 mt-2">Documents successfully printed</p>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Growth Rate</h3>
                <div className="flex items-center justify-center">
                  <div className={`text-5xl font-bold ${overview.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {overview.growth_rate > 0 ? '+' : ''}{overview.growth_rate.toFixed(1)}%
                  </div>
                </div>
                <p className="text-center text-gray-600 mt-2">Compared to previous period</p>
              </div>
            </div>

            {/* Revenue Chart */}
            {revenueData.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
                <Line
                  data={{
                    labels: revenueData.map(d => d.date),
                    datasets: [
                      {
                        label: 'Total Revenue',
                        data: revenueData.map(d => d.total_revenue),
                        borderColor: 'rgb(59, 130, 246)',
                        backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        tension: 0.4,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { position: 'top' },
                      title: { display: false },
                    },
                    scales: {
                      y: { beginAtZero: true },
                    },
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Shops Tab */}
        {activeTab === 'shops' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold">Top Performing Shops</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shop Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {shopRankings.map((shop) => (
                      <tr key={shop.shop_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                            shop.rank === 1 ? 'bg-yellow-100 text-yellow-800' :
                            shop.rank === 2 ? 'bg-gray-100 text-gray-800' :
                            shop.rank === 3 ? 'bg-orange-100 text-orange-800' :
                            'bg-blue-50 text-blue-800'
                          }`}>
                            {shop.rank}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{shop.shop_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">{shop.city}</td>
                        <td className="px-6 py-4 whitespace-nowrap">{(shop.documents || 0).toLocaleString()}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-green-600">
                          ₹{(shop.revenue || 0).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <span className="text-yellow-400 mr-1">★</span>
                            {(shop.rating || 0).toFixed(1)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            (shop.success_rate || 0) >= 90 ? 'bg-green-100 text-green-800' :
                            (shop.success_rate || 0) >= 70 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {(shop.success_rate || 0).toFixed(1)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Shop Performance Chart */}
            {shopRankings.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Revenue by Shop</h3>
                <Bar
                  data={{
                    labels: shopRankings.map(s => s.shop_name),
                    datasets: [
                      {
                        label: 'Revenue (₹)',
                        data: shopRankings.map(s => s.revenue),
                        backgroundColor: 'rgba(59, 130, 246, 0.6)',
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: { beginAtZero: true },
                    },
                  }}
                />
              </div>
            )}
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            {revenueData.length > 0 && (
              <>
                {/* Revenue Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <StatCard
                    title="Printing Revenue"
                    value={`₹${revenueData.reduce((sum, d) => sum + d.printing_revenue, 0).toLocaleString()}`}
                    subtitle="Current phase"
                    icon="🖨️"
                  />
                  <StatCard
                    title="Subscription Revenue"
                    value={`₹${revenueData.reduce((sum, d) => sum + d.subscription_revenue, 0).toLocaleString()}`}
                    subtitle="Phase 2 (Future)"
                    icon="💳"
                  />
                  <StatCard
                    title="Ad Revenue"
                    value={`₹${revenueData.reduce((sum, d) => sum + d.ad_revenue, 0).toLocaleString()}`}
                    subtitle="Phase 3 (Future)"
                    icon="📢"
                  />
                </div>

                {/* Revenue Trend Chart */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Multi-Source Revenue Trend</h3>
                  <Line
                    data={{
                      labels: revenueData.map(d => d.date),
                      datasets: [
                        {
                          label: 'Printing',
                          data: revenueData.map(d => d.printing_revenue),
                          borderColor: 'rgb(59, 130, 246)',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                        },
                        {
                          label: 'Subscriptions',
                          data: revenueData.map(d => d.subscription_revenue),
                          borderColor: 'rgb(16, 185, 129)',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                        },
                        {
                          label: 'Ads',
                          data: revenueData.map(d => d.ad_revenue),
                          borderColor: 'rgb(245, 158, 11)',
                          backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        },
                      ],
                    }}
                    options={{
                      responsive: true,
                      plugins: {
                        legend: { position: 'top' },
                      },
                      scales: {
                        y: { beginAtZero: true },
                      },
                    }}
                  />
                </div>

                {/* Revenue Distribution */}
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-4">Revenue Distribution</h3>
                  <div className="max-w-md mx-auto">
                    <Doughnut
                      data={{
                        labels: ['Printing', 'Subscriptions', 'Ads'],
                        datasets: [
                          {
                            data: [
                              revenueData.reduce((sum, d) => sum + d.printing_revenue, 0),
                              revenueData.reduce((sum, d) => sum + d.subscription_revenue, 0),
                              revenueData.reduce((sum, d) => sum + d.ad_revenue, 0),
                            ],
                            backgroundColor: [
                              'rgba(59, 130, 246, 0.8)',
                              'rgba(16, 185, 129, 0.8)',
                              'rgba(245, 158, 11, 0.8)',
                            ],
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: { position: 'bottom' },
                        },
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Geographic Tab */}
        {activeTab === 'geographic' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Geographic Analytics</h3>
            <p className="text-gray-600">
              Geographic heatmap and location-based analytics coming soon.
              This will show Zone → State → District → City → Area hierarchy with performance metrics.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  title: string;
  value: string | number;
  subtitle: string;
  icon: string;
}> = ({ title, value, subtitle, icon }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold mt-2">{value}</p>
        <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
      </div>
      <div className="text-4xl">{icon}</div>
    </div>
  </div>
);

export default AdminAnalytics;
